import { useCallback, useEffect, useRef, useState } from "react"

interface SkullWithEyesProps {
  className?: string
}

export default function SkullWithEyes({ className }: SkullWithEyesProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [tilt, setTilt] = useState({ x: 0, y: 0 })
  const [leftEyeOffset, setLeftEyeOffset] = useState({ x: 0, y: 0 })
  const [rightEyeOffset, setRightEyeOffset] = useState({ x: 0, y: 0 })
  const [isBlinking, setIsBlinking] = useState(false)

  const leftSocketRef = useRef<HTMLDivElement>(null)
  const rightSocketRef = useRef<HTMLDivElement>(null)

  const maxTilt = 25
  const eyeMovementRadius = 12

  const updateEye = useCallback(
    (
      socketRef: React.RefObject<HTMLDivElement | null>,
      targetX: number,
      targetY: number,
    ) => {
      if (!socketRef.current) return { x: 0, y: 0 }

      const rect = socketRef.current.getBoundingClientRect()
      const socketCenterX = rect.left + rect.width / 2
      const socketCenterY = rect.top + rect.height / 2

      const angle = Math.atan2(targetY - socketCenterY, targetX - socketCenterX)
      const dist = Math.min(
        eyeMovementRadius,
        Math.hypot(targetX - socketCenterX, targetY - socketCenterY),
      )

      return {
        x: Math.cos(angle) * dist,
        y: Math.sin(angle) * dist,
      }
    },
    [],
  )

  const trackPointer = useCallback(
    (clientX: number, clientY: number) => {
      const mouseX = clientX
      const mouseY = clientY

      const cx = window.innerWidth / 2
      const cy = window.innerHeight / 2

      const dxPct = (mouseX - cx) / cx
      const dyPct = (mouseY - cy) / cy

      const tiltY = dxPct * maxTilt
      const tiltX = -dyPct * maxTilt

      setTilt({ x: tiltX, y: tiltY })

      setLeftEyeOffset(updateEye(leftSocketRef, mouseX, mouseY))
      setRightEyeOffset(updateEye(rightSocketRef, mouseX, mouseY))
    },
    [maxTilt, updateEye],
  )

  useEffect(() => {
    const handlePointer = (event: PointerEvent) => {
      trackPointer(event.clientX, event.clientY)
    }

    const handleMouse = (event: MouseEvent) => {
      trackPointer(event.clientX, event.clientY)
    }

    const handleTouch = (event: TouchEvent) => {
      const touch = event.touches[0] ?? event.changedTouches[0]
      if (!touch) return
      trackPointer(touch.clientX, touch.clientY)
    }

    window.addEventListener("pointermove", handlePointer, { passive: true })
    window.addEventListener("pointerdown", handlePointer, { passive: true })
    window.addEventListener("mousemove", handleMouse, { passive: true })
    window.addEventListener("touchmove", handleTouch, { passive: true })
    window.addEventListener("touchstart", handleTouch, { passive: true })
    return () => {
      window.removeEventListener("pointermove", handlePointer)
      window.removeEventListener("pointerdown", handlePointer)
      window.removeEventListener("mousemove", handleMouse)
      window.removeEventListener("touchmove", handleTouch)
      window.removeEventListener("touchstart", handleTouch)
    }
  }, [trackPointer])

  const triggerBlink = useCallback(() => {
    setIsBlinking(true)
    setTimeout(() => setIsBlinking(false), 150)
  }, [])

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>

    const scheduleNextBlink = () => {
      const delay = 2000 + Math.random() * 3000
      timeoutId = setTimeout(() => {
        triggerBlink()
        scheduleNextBlink()
      }, delay)
    }

    const initialDelay = setTimeout(() => {
      triggerBlink()
      scheduleNextBlink()
    }, 1500)

    return () => {
      clearTimeout(initialDelay)
      clearTimeout(timeoutId)
    }
  }, [triggerBlink])

  const getPupilStyle = (offset: { x: number; y: number }) => ({
    transform: `translate(-50%, -50%) translate(${offset.x}px, ${offset.y}px)`,
    height: isBlinking ? "0px" : undefined,
    opacity: isBlinking ? 0.5 : 1,
    boxShadow: isBlinking ? "none" : undefined,
  })

  return (
    <div
      ref={containerRef}
      className={`css-skull-perspective relative flex items-center justify-center ${className}`}
    >
      <div
        className="css-skull-pivot"
        style={{
          transform: `rotateY(${tilt.y}deg) rotateX(${tilt.x}deg)`,
        }}
      >
        <div className="css-skull-bouncer">
          {/* Cranium */}
          <div className="css-skull-cranium">
            {/* Left Eye Socket */}
            <div ref={leftSocketRef} className="css-skull-eye-socket left">
              <div
                className="css-skull-pupil"
                style={getPupilStyle(leftEyeOffset)}
              />
            </div>

            {/* Right Eye Socket */}
            <div ref={rightSocketRef} className="css-skull-eye-socket right">
              <div
                className="css-skull-pupil"
                style={getPupilStyle(rightEyeOffset)}
              />
            </div>

            {/* Nose */}
            <div className="css-skull-nose" />
          </div>

          {/* Jaw & Teeth */}
          <div className="css-skull-jaw">
            <div className="css-skull-teeth-row">
              <div className="css-skull-tooth" />
              <div className="css-skull-tooth" />
              <div className="css-skull-tooth" />
              <div className="css-skull-tooth" />
              <div className="css-skull-tooth" />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
